/**
 * Chooses the version of one judge prompt for a run or a re-grade. An empty value means
 * the version that is current when the run is queued.
 */
import { Link } from 'react-router-dom';
import type { JudgePromptSummary } from '../types/evalTypes';

interface JudgeVersionSelectProps {
  judge: JudgePromptSummary;
  /** A version ID, or '' for the current version. */
  value: string;
  disabled: boolean;
  onChange: (versionId: string) => void;
}

export const JudgeVersionSelect = ({ judge, value, disabled, onChange }: JudgeVersionSelectProps) => {
  const current = judge.versions.find((version) => version.versionId === judge.currentVersionId);
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">
        Judge prompt: <span className="font-mono">{judge.judgeName}</span>{' '}
        <Link to={`/evals/judge-prompts?suite=${judge.suiteId}&judge=${judge.judgeName}`} className="font-normal text-blue-600 hover:underline">
          view versions
        </Link>
      </span>
      <select
        value={value}
        disabled={disabled || judge.versions.length === 0}
        onChange={(event) => onChange(event.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
      >
        <option value="">{current ? `Current (v${current.number})` : 'No prompt yet'}</option>
        {judge.versions
          .filter((version) => version.versionId !== judge.currentVersionId && !version.archived)
          .map((version) => (
            <option key={version.versionId} value={version.versionId}>
              v{version.number}
              {version.tokenName !== undefined ? ' (Claude)' : ''}
              {version.note ? ` · ${version.note.slice(0, 80)}` : ''}
            </option>
          ))}
      </select>
    </label>
  );
};

/** Sets or, with '', clears the chosen version of one judge. */
export const withJudgeVersion = (previous: Record<string, string>, judgeName: string, versionId: string): Record<string, string> => {
  const others = Object.entries(previous).filter(([name]) => name !== judgeName);
  return Object.fromEntries(versionId === '' ? others : [...others, [judgeName, versionId]]);
};
